import {
  FileUploadConfig,
  isFileUploadValidationError,
  ProjectDecisionAttachment,
  toFileWithId,
  UploadedAttachment,
  validateFiles,
} from "@/fileUpload/fileUploadDomain";
import {
  AttachmentType,
  DbotEvent,
  DbotEventDispatcher,
  DeleteAttachmentEvent,
  deleteAttachmentSuccessEvent,
  DocumentContext,
  ProjectContext,
  supportsAttachments,
  UploadAttachmentsEvent,
  UploadingAttachment,
} from "./common/commonDomain";
import { logDebug } from "@/utils/log";
import { AxiosInstance } from "axios";
import { createFileTypeValidationErrorMessage } from "@/fileUpload/fileUploadErrorMessages";
import { getFilenameFromHeaders } from "@/api";
import { DecisionAndForm, DocumentAndForm, DocumentData } from "./project/projectDomain";
import {
  AttachmentPanelProps,
  DecisionAttachmentPanelProps,
} from "@/components-v2/AttachmentsPanel";
import { SelectFilesButtonProps } from "@/components-v2/SelectFilesButton";
import { UploadFilesPanelProps } from "@/components-v2/UploadFilesPanel";
import { action, runInAction } from "mobx";
import { remove } from "lodash";
import { DocumentType } from "@/common/documents";
import { DecisionType } from "@/common/decisions";
import {
  confirmAction,
  showAlert,
  showError,
  showGeneralError,
  showSuccess,
  thunk,
} from "@/events/eventsDomain";
import { isProductionEnvironment } from "@/utils/environments";
import { TMessage } from "@/i18n/tt";
import { IconProps } from "@/components-v2/Icon";
import {
  getExtension,
  isExtensionImage,
  isExtensionOffice,
  isSupportedFileTypeForPreview,
} from "@/utils/files";
import { toNumber } from "@/utils/numbers";
import { ConversionModel, supportedFiletypesForConversion } from "@/fileUpload/ConversionModel";

export type UploadedAttachmentProps = UploadedAttachment & {
  source?: TMessage;
  icon?: IconProps["name"];
  downloadOriginalUrl?: string;
};

const runningConversions = new Map<string, ConversionModel>();

export const keepOriginalFormat = (
  projectId: string,
  attachment: UploadedAttachment | ProjectDecisionAttachment,
  dispatch: DbotEventDispatcher,
  http: AxiosInstance,
) => {
  if (attachment.isConverting) {
    runningConversions.get(attachment.id)?.onCancel();
    return;
  }
  http
    .post<UploadedAttachment>(`/project/${projectId}/attachment/${attachment.id}/restore-original`)
    .then((res) =>
      dispatch([
        {
          type: "dbot/update-uploading-attachment-props",
          reference: { type: "attachmentId", id: attachment.id },
          filename: res.data.filename,
          originalFileType: undefined,
        },
        showSuccess({
          key: "attachments.keep-original-format.success",
          params: { fileName: res.data.filename },
        }),
      ]),
    )
    .catch(() => dispatch([showGeneralError()]));
};

export function triggerConversionStatus(
  uploads: Pick<UploadingAttachment, "id" | "filename">[],
  http: AxiosInstance,
  dispatch: DbotEventDispatcher,
  documentConversionIdForStatusCheck: string | undefined = undefined,
) {
  uploads.forEach((upload) => {
    const model = new ConversionModel(
      http,
      { type: "attachmentId", id: upload.id },
      upload.filename,
      dispatch,
    );
    runningConversions.set(upload.id, model);
    const onFailure = () => {
      dispatch([
        showAlert(
          {
            key: "file-preview.convert-to-pdf.error",
            params: { filename: upload.filename },
          },
          "info",
        ),
      ]);
    };
    if (documentConversionIdForStatusCheck) {
      model.checkStatusInBackground(true, undefined, documentConversionIdForStatusCheck, onFailure);
    } else {
      model.onConvert(true, undefined, onFailure);
    }
  });
}

export const uploadAttachments = (
  projectId: string,
  event: UploadAttachmentsEvent,
  fileUploadConfig: FileUploadConfig,
  dispatch: DbotEventDispatcher,
  http: AxiosInstance,
  documents: DocumentAndForm[],
  decisions: DecisionAndForm<DecisionType>[],
) => {
  if (!event.files.length) {
    return;
  }
  const document = documents.find((d) => d.documentId === event.reference);
  const decision = decisions.find((d) => d.decisionId === event.reference);
  const shouldTrackUploads = Boolean(document || decision);
  let conversionUploads: Pick<UploadingAttachment, "id" | "filename">[] = [];
  const isUploadingToNotice =
    document &&
    (document.type === DocumentType.notice_board_meeting ||
      document.type === DocumentType.notice_general_meeting);
  validateFiles(event.files, fileUploadConfig)
    .then((files) => {
      const filesWithIds = files.map(toFileWithId);
      conversionUploads = filesWithIds
        .filter(
          ({ file }) =>
            isExtensionImage(file.name) || (isExtensionOffice(file.name) && !isUploadingToNotice),
        )
        .map(({ id, file }) => ({ id, filename: file.name }));
      if (shouldTrackUploads) {
        const uploads: UploadingAttachment[] = filesWithIds.map(({ id, file }) => ({
          id,
          filename: file.name,
          progress: 0,
          reference: event.reference,
        }));
        dispatch({
          type: "dbot/change-state",
          state: {
            type: "uploading-attachments",
            reference: event?.reference,
            uploads,
          },
        });
      }
      const formData = new FormData();
      for (const { id, file } of filesWithIds) {
        formData.append("files", file);
        formData.append("ids", id);
      }
      formData.set("reference", event.reference);
      formData.set("attachmentType", event.attachmentType);
      formData.set(
        "overrideExistingAttachmentType",
        `${event.overrideExistingAttachmentType ?? false}`,
      );
      const total = files.reduce((sum, file) => sum + file.size, 0);
      const toProgressByFile = (loaded: number) => {
        let remaining = loaded;
        return files.map((file) => {
          const fileLoaded = Math.min(file.size, Math.max(0, remaining));
          remaining -= fileLoaded;
          return file.size ? Math.min(1, fileLoaded / file.size) : 1;
        });
      };
      return http.post<UploadedAttachment[]>(`/project/${projectId}/attachment`, formData, {
        onUploadProgress: (progressEvent) => {
          if (!shouldTrackUploads) {
            return;
          }
          const { progress, bytes, loaded } = progressEvent;
          const uploadProgress = calculateProgress(total, 0, progress, bytes, loaded);
          if (!uploadProgress) {
            return;
          }
          const progressByFile = toProgressByFile(uploadProgress);
          dispatch({
            type: "dbot/update-state",
            update: (state) => {
              if (state.type !== "uploading-attachments" || state.reference !== event.reference) {
                return;
              }
              state.uploads.forEach((upload, index) => {
                const progress = progressByFile[index];
                if (progress !== undefined) {
                  upload.progress = progress;
                }
              });
            },
          });
        },
      });
    })
    .then(
      action((res) => {
        if (event.attachmentType === AttachmentType.UPLOADED_FILE) {
          if (document) {
            document.attachments.push(...res.data);
          }
          if (decision) {
            decision.attachments.push(
              ...res.data.map((a) => ({
                ...a,
                selectedDocuments: [...(event.selectedDocuments ?? [])],
              })),
            );
          }
        }
        event.onUploaded?.(res.data);
        if (event.onSuccess) {
          dispatch(event.onSuccess);
        }
        if (shouldTrackUploads && conversionUploads.length > 0) {
          triggerConversionStatus(conversionUploads, http, dispatch);
        }
      }),
    )
    .catch((err) => {
      if (isFileUploadValidationError(err)) {
        logDebug(err, "Invalid attachments", {
          reference: event.reference,
          attachmentType: event.attachmentType,
        });
        dispatch([showError(createFileTypeValidationErrorMessage(err))]);
      } else {
        dispatch([showGeneralError()]);
      }
    })
    .finally(() => {
      if (shouldTrackUploads) {
        dispatch({ type: "dbot/change-state", state: { type: "ready" } });
      }
    });
};

export const deleteAttachment = (
  projectId: string,
  event: DeleteAttachmentEvent,
  dispatch: DbotEventDispatcher,
  http: AxiosInstance,
  documents: DocumentAndForm[],
  decisions: DecisionAndForm<DecisionType>[],
) => {
  const document = documents.find((d) => d.documentId === event.reference);
  const decision = decisions.find((d) => d.decisionId === event.reference);

  if (document || decision) {
    dispatch({
      type: "dbot/change-state",
      state: {
        type: "deleting-attachment",
        attachmentId: event.attachmentId,
        reference: event?.reference,
      },
    });
  }
  const ready: DbotEvent = { type: "dbot/change-state", state: { type: "ready" } };
  const onDeleted = () => {
    dispatch(deleteAttachmentSuccessEvent(event.attachmentId, event.source, event.reference));
    if (event.onSuccess) {
      dispatch(event.onSuccess);
    }
  };
  const isDocumentAttachment = document?.attachments.some((a) => a.id === event.attachmentId);
  if (document && !isDocumentAttachment) {
    // Attachment is coming via a decision, but deleting is happening via a document.
    //We need to simulate the same process of unselecting a document when an attachment is uploaded and a document is selected/checked.
    const decisionAndForm = decisions.find((d) =>
      d.attachments.some((a) => a.id === event.attachmentId),
    );
    if (decisionAndForm) {
      runInAction(() => {
        const attachment = decisionAndForm.attachments.find((a) => a.id === event.attachmentId);
        if (!attachment) {
          return;
        }
        const selectedIndex = attachment.selectedDocuments.findIndex(
          (type) => type === document.type,
        );
        if (selectedIndex >= 0) {
          attachment.selectedDocuments.splice(selectedIndex, 1);
        }
      });
    }
    onDeleted();
    dispatch(ready);
    return;
  }
  const owner =
    document ??
    (decision?.attachments.some((a) => a.id === event.attachmentId) ? decision : undefined);
  if (owner) {
    runInAction(() => {
      remove(owner.attachments, (a) => a.id === event.attachmentId);
    });
    onDeleted();
    dispatch(ready);
    return;
  }
  http
    .delete(`/project/${projectId}/attachment/${event.attachmentId}`)
    .then(onDeleted)
    .catch(() => {
      dispatch([showError({ key: "dbot.attachment.attachmentDeleteError" })]);
    })
    .finally(() => {
      dispatch(ready);
    });
};

export const downloadAttachment = ({
  projectId,
  attachmentId,
  http,
}: {
  projectId: string;
  attachmentId: string;
  http: AxiosInstance;
}): Promise<{ data: Blob; filename: string | undefined }> =>
  http
    .post(`/project/${projectId}/attachment/${attachmentId}/download`, null, {
      responseType: "blob",
    })
    .then((res) => ({ data: res.data, filename: getFilenameFromHeaders(res.headers) }));

export const uploadingAttachments = (context: DocumentContext<DocumentData>) =>
  context.state.type === "uploading-attachments" && context.state.reference === context.documentId
    ? context.state.uploads
    : undefined;

export const prepDocumentAttachmentsPanel = (
  context: DocumentContext<DocumentData>,
  dispatch: DbotEventDispatcher,
): AttachmentPanelProps | undefined =>
  (context.isLocked && !context.attachments.length) || !supportsAttachments(context)
    ? undefined
    : {
        title: false,
        autoAttachments: context.autoAttachments,
        uploadedAttachments: context.attachments,
        uploadingAttachments: uploadingAttachments(context),
        fileUploadConfig: context.fileUploadConfig,
        isDeleting: (id) =>
          context.state.type === "deleting-attachment" && context.state.attachmentId === id,
        onOpen: (attachmentId) => {
          const isAutoAttachment = context.autoAttachments?.some((a) => a.type === attachmentId);
          if (!isProductionEnvironment() || isAutoAttachment) {
            const attachment = context.attachments.find((a) => a.id === attachmentId);
            if (isSupportedFileTypeForPreview(attachment?.filename) || isAutoAttachment) {
              dispatch({ type: "dbot/open-attachment-preview", attachmentId });
            } else {
              dispatch({ type: "dbot/open-attachment", attachmentId });
            }
          } else {
            dispatch({ type: "dbot/open-attachment", attachmentId });
          }
        },
        onMove: context.isLocked
          ? undefined
          : (attachment, toAttachmentId) =>
              dispatch(
                attachment.decisionId
                  ? {
                      type: "dbot/move-decision-attachment",
                      decisionId: attachment.decisionId,
                      attachmentId: attachment.id,
                      toAttachmentId,
                    }
                  : {
                      type: "dbot/move-document-attachment",
                      documentId: context.documentId,
                      attachmentId: attachment.id,
                      toAttachmentId,
                    },
              ),
        onDelete:
          context.isLocked && context.documentData.type !== DocumentType.notice_general_meeting
            ? undefined
            : (attachmentId) =>
                dispatch(
                  confirmAction({
                    title: { key: "confirm-delete" },
                    text: {
                      key: "general.confirm-delete",
                      params: {
                        name: context.attachments.find((a) => a.id === attachmentId)!.filename,
                      },
                    },
                    confirmLabel: { key: "yes" },
                    cancelLabel: { key: "no" },
                    onConfirm: [
                      thunk(() =>
                        dispatch({
                          type: "dbot/delete-attachment",
                          attachmentId,
                          reference: context.documentId,
                          source: context.documentId,
                        }),
                      ),
                    ],
                  }),
                ),
        onKeepOriginalFormat: context.isLocked
          ? undefined
          : (attachmentId) => dispatch({ type: "dbot/keep-original-format", attachmentId }),
      };

export const prepUploadDocumentAttachmentsButton = (
  context: DocumentContext<DocumentData>,
  dispatch: DbotEventDispatcher,
): SelectFilesButtonProps | undefined =>
  supportsAttachments(context)
    ? {
        inputId: `attachments-input-${context.documentId}`,
        variant: "link",
        icon: "upload",
        label: { key: "upload-attachments" },
        fileUploadConfig: context.fileUploadConfig,
        isDisabled:
          context.state.type === "uploading-attachments" &&
          context.state.reference === context.documentId,
        onChange: (files) =>
          dispatch({
            type: "dbot/upload-attachments",
            attachmentType: AttachmentType.UPLOADED_FILE,
            files,
            reference: context.documentId,
            source: context.documentId,
          }),
      }
    : undefined;

export const decisionAttachmentDocumentTypes = (context: ProjectContext): DocumentType[] =>
  context.currentDocuments
    .filter(
      (d) =>
        d.type !== DocumentType.custom_document && d.type !== DocumentType.articlesofassociation,
    )
    .map((d) => d.type);

export const prepDecisionAttachmentsPanel = (
  context: ProjectContext,
  decisionId: string,
  attachments: ProjectDecisionAttachment[],
  dispatch: DbotEventDispatcher,
): DecisionAttachmentPanelProps | undefined =>
  supportsAttachments(context)
    ? {
        title: true,
        uploadingAttachments:
          context.state.type === "uploading-attachments" && context.state.reference === decisionId
            ? context.state.uploads
            : undefined,
        uploadedAttachments: attachments,
        currentDocumentTypes: decisionAttachmentDocumentTypes(context),
        fileUploadConfig: context.fileUploadConfig,
        isDeleting: (id) =>
          context.state.type === "deleting-attachment" && context.state.attachmentId === id,
        onOpen: (attachmentId) => dispatch({ type: "dbot/open-attachment", attachmentId }),
        onDelete: context.isLocked
          ? undefined
          : (attachmentId) =>
              dispatch({
                type: "dbot/delete-attachment",
                attachmentId,
                reference: decisionId,
                source: decisionId,
              }),
        onKeepOriginalFormat: context.isLocked
          ? undefined
          : (attachmentId) => dispatch({ type: "dbot/keep-original-format", attachmentId }),
        onToggleDocumentType: context.isLocked
          ? undefined
          : (attachment, documentType) => {
              runInAction(() => {
                const index = attachment.selectedDocuments.findIndex((d) => d === documentType);
                if (index >= 0) {
                  attachment.selectedDocuments.splice(index, 1);
                } else {
                  attachment.selectedDocuments.push(documentType);
                }
              });
            },
        isLocked: context.isLocked,
      }
    : undefined;

export const prepUploadDecisionAttachmentsButton = (
  context: ProjectContext,
  decisionId: string,
  dispatch: DbotEventDispatcher,
): SelectFilesButtonProps | undefined =>
  supportsAttachments(context)
    ? {
        variant: "secondary",
        icon: "upload",
        label: { key: "upload-attachments" },
        fileUploadConfig: context.fileUploadConfig,
        isDisabled:
          context.isLocked ||
          (context.state.type === "uploading-attachments" &&
            context.state.reference === decisionId),
        onChange: (files) =>
          dispatch({
            type: "dbot/upload-attachments",
            attachmentType: AttachmentType.UPLOADED_FILE,
            files,
            reference: decisionId,
            source: decisionId,
          }),
      }
    : undefined;

export const prepDecisionAttachmentsDropzone = (
  context: ProjectContext,
  decisionId: string,
  dispatch: DbotEventDispatcher,
  onUploaded?: (attachments: UploadedAttachment[]) => void,
): UploadFilesPanelProps | undefined =>
  supportsAttachments(context)
    ? {
        fileUploadConfig: context.fileUploadConfig,
        isUploading:
          context.state.type === "uploading-attachments" && context.state.reference === decisionId,
        onUpload: (files) =>
          dispatch({
            type: "dbot/upload-attachments",
            attachmentType: AttachmentType.UPLOADED_FILE,
            files,
            reference: decisionId,
            source: decisionId,
            selectedDocuments: decisionAttachmentDocumentTypes(context),
            onUploaded,
          }),
      }
    : undefined;

export const calculateProgress = (
  totalBytes: number,
  uploadedBytes: number,
  eventProgress: number | undefined,
  progressBytes: number | undefined,
  progressLoaded: number | undefined,
): number | undefined => {
  const rawProgress = toNumber(eventProgress, 0);
  const normalizedProgress = rawProgress > 1 ? rawProgress / 100 : rawProgress;
  const progress = Math.max(0, Math.min(1, normalizedProgress));
  let loaded = progress * totalBytes;

  if (loaded === 0 && toNumber(progressBytes) !== undefined) {
    loaded = Math.min(totalBytes, uploadedBytes + progressBytes!);
  }
  if (loaded === 0 && toNumber(progressLoaded) !== undefined) {
    loaded = progressLoaded!;
  }

  if (loaded === 0) {
    return undefined;
  }
  loaded = Math.min(Math.max(0, loaded), totalBytes);
  return loaded;
};

export const showsFilePreview = (filename: string, canConvert: boolean): boolean =>
  isSupportedFileTypeForPreview(filename) &&
  !(canConvert && supportedFiletypesForConversion.includes(getExtension(filename) ?? ""));
